export function inIFrame() {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
}

export const deepMerge = (target = {}, source) => {
  for (const key of Object.keys(source)) {
    if (source[key] instanceof Object)
      Object.assign(source[key], deepMerge(target[key], source[key]));
  }
  Object.assign(target || {}, source);
  return target;
};

export function getBrowserLocales() {
  return navigator.languages && navigator.languages.length
    ? navigator.languages
    : [navigator.language];
}

/**
 * Извлекает сообщение(я) об ошибке из ответа сервера.
 * Возвращает строку для единичной ошибки либо массив строк, когда сервер
 * присылает список ошибок (`data` — массив объектов с `message`).
 * Обрабатывать оба случая: см. `useResponseErrorMessage`.
 */
export function getResponseErrorMessage(response) {
  const { data } = response;
  if (!data) return response.message;
  if (data.message) return data.message;
  if (Array.isArray(data) && data.length && data[0].message) {
    return data.map((item) => item.message).filter(Boolean);
  }
  return response.message;
}

export function isInsideTgMiniApp() {
  return !!window.TelegramWebviewProxy
    || (window.Telegram?.WebApp?.initData?.length > 0);
}

export function digitsOnly(value) {
  return String(value || '').replace(/\D/g, '');
}

/**
 * Компактный формат числа: значения от 1000 отображаются с суффиксом `k`
 * (например `1000 → 1k`, `1500 → 1.5k`, `3000 → 3k`); меньшие — как есть.
 */
export function formatCompactNumber(value) {
  if (value == null || !Number.isFinite(Number(value))) return '';
  if (value >= 1000) {
    const thousands = value / 1000;
    return `${Number.isInteger(thousands) ? thousands : thousands.toFixed(1)}k`;
  }
  return `${value}`;
}

/**
 * Форматирует денежную сумму, группируя разряды неразрывным пробелом
 * (например `10000 → 10 000`, `1234567 → 1 234 567`). Дробная часть сохраняется.
 * Используется для баланса, порога вывода и сумм начислений/списаний.
 */
export function formatAmount(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return `${value ?? ''}`;
  const [intPart, fracPart] = String(number).split('.');
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return fracPart ? `${grouped}.${fracPart}` : grouped;
}

/**
 * Нормализует PAN к строке с разделителями между группами по 4 цифры (например `4000 0012 3456 7899`).
 * В payload запросов к бэкенду (например `cardNumber` в `/transaction/expense`) значение должно
 * передаваться в таком формате: требование бизнеса к контракту API, а не только отображение в UI.
 */
export function formatCardNumberWithSpaces(cardString) {
  const card = digitsOnly(cardString);
  if (!card.length) return '';
  return card.match(/.{1,4}/g)?.join(' ') ?? card;
}

function isMastercardTwoSeries(digits) {
  const prefix = Number(String(digits).slice(0, 4));
  return prefix >= 2221 && prefix <= 2720;
}

export function detectCardNetwork(digits) {
  const d = digitsOnly(digits);
  if (!d) return null;
  if (/^220[0-4]/.test(d)) return { id: 'mir', name: 'Мир' };
  if (/^3[47]/.test(d)) return { id: 'amex', name: 'American Express' };
  if (/^(5018|5020|5038|5893|6304|6759|676[123])/.test(d)) return { id: 'maestro', name: 'Maestro' };
  if (/^5[1-5]/.test(d) || isMastercardTwoSeries(d)) return { id: 'mastercard', name: 'Mastercard' };
  if (/^6(?:011|5)/.test(d)) return { id: 'discover', name: 'Discover' };
  if (/^62/.test(d)) return { id: 'unionpay', name: 'UnionPay' };
  if (/^4/.test(d)) return { id: 'visa', name: 'Visa' };
  return null;
}

const NETWORK_CARD_LENGTH = { amex: 15 };

export function getExpectedCardLength(cardString) {
  const network = detectCardNetwork(digitsOnly(cardString));
  return NETWORK_CARD_LENGTH[network?.id] ?? 16;
}

export function isCardNumberValid(cardString) {
  const card = digitsOnly(cardString);
  const network = detectCardNetwork(card);
  if (!network) return card.length === 16 && checkCard(card);
  const expected = NETWORK_CARD_LENGTH[network.id] ?? 16;
  return card.length === expected && checkCard(card);
}

export function checkCard(cardString) {
  const card = digitsOnly(cardString);
  if (!card.length) return false;
  let sum = 0;
  const parity = card.length % 2;
  for (let i = 0; i < card.length; i++) {
    let digit = Number(card.charAt(i));
    if (Number.isNaN(digit)) return false;
    if (i % 2 === parity) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
  }
  return sum % 10 === 0;
}

export {
  getVkOAuthRedirectOrigin,
  buildVkIdRedirectUri,
  getVkIdTokenExchangeUrl,
  extractVkIdAuthToken,
  replaceLocationWithVkPanelToken,
  isVkOAuthCallbackWithParams,
  VK_OAUTH_PKCE_TTL_MS,
  clearVkOAuthPkceSession,
  writeVkOAuthPkceSession,
  readVkOAuthPkceSession,
  startVkIdLinkingFlow,
  readVkOAuthLinkReturnUrl,
  consumeVkOAuthLinkReturnUrl,
  markVkOAuthFlowAsAuth,
  markVkOAuthFlowAsLink,
  isVkOAuthLinkFlow,
  clearVkOAuthFlowMode,
} from './vkOAuth';
